import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Box, CssBaseline } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

type ColorMode = "light" | "dark";

type ThemeModeContextValue = {
    mode: ColorMode;
    toggleColorMode: () => void;
    setColorModeEnabled: (enabled: boolean) => void;
    sidebarFontSize: number;
    increaseSidebarFontSize: () => void;
    decreaseSidebarFontSize: () => void;
};

const THEME_STORAGE_KEY = "gtr-color-mode";
const SIDEBAR_FONT_STORAGE_KEY = "gtr-sidebar-font-size";
const DEFAULT_SIDEBAR_FONT_SIZE = 12;
const MIN_SIDEBAR_FONT_SIZE = 12;
const MAX_SIDEBAR_FONT_SIZE = 16;

const ThemeModeContext = createContext<ThemeModeContextValue>({
    mode: "light",
    toggleColorMode: () => undefined,
    setColorModeEnabled: () => undefined,
    sidebarFontSize: DEFAULT_SIDEBAR_FONT_SIZE,
    increaseSidebarFontSize: () => undefined,
    decreaseSidebarFontSize: () => undefined,
});

const getInitialMode = (): ColorMode => {
    try {
        const storedMode = localStorage.getItem(THEME_STORAGE_KEY);
        return storedMode === "dark" ? "dark" : "light";
    } catch {
        return "light";
    }
};

const getInitialSidebarFontSize = (): number => {
    try {
        const storedFontSize = Number(localStorage.getItem(SIDEBAR_FONT_STORAGE_KEY));
        if (Number.isFinite(storedFontSize)) {
            return Math.min(MAX_SIDEBAR_FONT_SIZE, Math.max(MIN_SIDEBAR_FONT_SIZE, storedFontSize));
        }
    } catch {
        // Mantém o tamanho padrão quando o storage está indisponível.
    }

    return DEFAULT_SIDEBAR_FONT_SIZE;
};

export const useThemeMode = () => useContext(ThemeModeContext);

/**
 * Mantém o tema visual centralizado sem alterar estado ou regras das telas.
 */
const DarkModeWrapper = ({ children }: { children: ReactNode }) => {
    const [mode, setMode] = useState<ColorMode>(getInitialMode);
    const [colorModeEnabled, setColorModeEnabled] = useState(true);
    const [sidebarFontSize, setSidebarFontSize] = useState<number>(getInitialSidebarFontSize);
    const effectiveMode: ColorMode = colorModeEnabled ? mode : "light";

    const toggleColorMode = useCallback(() => {
        setMode((currentMode) => currentMode === "light" ? "dark" : "light");
    }, []);

    const increaseSidebarFontSize = useCallback(() => {
        setSidebarFontSize((currentSize) => Math.min(MAX_SIDEBAR_FONT_SIZE, currentSize + 1));
    }, []);

    const decreaseSidebarFontSize = useCallback(() => {
        setSidebarFontSize((currentSize) => Math.max(MIN_SIDEBAR_FONT_SIZE, currentSize - 1));
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        root.classList.toggle("dark", effectiveMode === "dark");
        root.dataset.theme = effectiveMode;
        root.style.colorScheme = effectiveMode;
    }, [effectiveMode]);

    useEffect(() => {
        try {
            localStorage.setItem(THEME_STORAGE_KEY, mode);
        } catch {
            // O tema continua funcional durante a sessão quando o storage está indisponível.
        }
    }, [mode]);

    useEffect(() => {
        try {
            localStorage.setItem(SIDEBAR_FONT_STORAGE_KEY, String(sidebarFontSize));
        } catch {
            // O controle continua funcional durante a sessão quando o storage está indisponível.
        }
    }, [sidebarFontSize]);

    const theme = useMemo(
        () => createTheme({
            palette: {
                mode: effectiveMode,
                primary: { main: "#006C77" },
                success: { main: "#00B700" },
                background: {
                    default: effectiveMode === "dark" ? "#0f172a" : "#003f4a",
                    paper: effectiveMode === "dark" ? "#111827" : "#ffffff",
                },
            },
            typography: { fontFamily: "Prometo, sans-serif" },
            components: {
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontFamily: "Prometo, sans-serif",
                            textTransform: "none",
                        },
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            fontFamily: "Prometo, sans-serif",
                            "&.Mui-focused": { color: "#00B700" },
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            borderRadius: 8,
                            fontFamily: "Prometo, sans-serif",
                            transition: "box-shadow 200ms ease, background-color 200ms ease",
                            "&:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#006C77",
                                borderWidth: 1,
                            },
                            "&.Mui-focused": {
                                boxShadow: "0 0 0 3px rgba(0,183,0,0.12)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#00B700",
                                borderWidth: 2,
                            },
                        },
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        paper: {
                            border: "none",
                            borderRadius: 12,
                            boxShadow: "none",
                        },
                    },
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: {
                            backgroundColor: effectiveMode === "dark" ? "#334155" : "rgba(15, 23, 42, 0.92)",
                            color: "#f8fafc",
                            fontFamily: "Prometo, sans-serif",
                            fontSize: 12,
                        },
                        arrow: {
                            color: effectiveMode === "dark" ? "#334155" : "rgba(15, 23, 42, 0.92)",
                        },
                    },
                },
            },
        }),
        [effectiveMode]
    );

    const contextValue = useMemo(
        () => ({
            mode,
            toggleColorMode,
            setColorModeEnabled,
            sidebarFontSize,
            increaseSidebarFontSize,
            decreaseSidebarFontSize,
        }),
        [
            mode,
            toggleColorMode,
            setColorModeEnabled,
            sidebarFontSize,
            increaseSidebarFontSize,
            decreaseSidebarFontSize,
        ]
    );

    return (
        <ThemeModeContext.Provider value={contextValue}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box className="gtr-theme-root tw-min-h-screen tw-bg-[#003f4a] tw-text-slate-900 tw-transition-colors tw-duration-300 dark:tw-bg-slate-950 dark:tw-text-slate-100">
                    {children}
                </Box>
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
};

export default DarkModeWrapper;
